// @ts-nocheck
/**
 * @file Common_Input.tsx
 * @description High-performance, premium input component adapted from sk-voice-fe.
 */

import React, { useState } from "react";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TextInputProps,
  Platform,
  StyleProp,
  ViewStyle,
} from "react-native";
import { Colors } from "../../theme/tokens";

export type CommonInputProps = Readonly<{
  label?: string;
  value: string;
  placeholder?: string;
  error?: string;
  touched?: boolean;
  onChangeText?: (text: string) => void;
  isValid?: (value: string) => boolean;
  secureTextEntry?: boolean;
  rightIcon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  disableCopyPaste?: boolean;
  inputProps?: TextInputProps;
  editable?: boolean;
  isDark?: boolean;
  isReadOnly?: boolean;
  badge?: string;
  containerStyle?: StyleProp<ViewStyle>;
  /** Helper text shown below the field (hidden while an error is showing). */
  hint?: string;
  /** Render as a multi-line text area (top-aligned, taller wrapper). */
  multiline?: boolean;
  numberOfLines?: number;
  /** Compact sizing — shorter wrapper, smaller label/font, rounded corners.
   *  Used inside dense surfaces like the in-modal Add-customer card. */
  dense?: boolean;
  /** Show a red required asterisk next to the label. */
  required?: boolean;
}>;

const InputLabel = ({ label, isDark, isReadOnly, badge, dense, required }: {
  label?: string;
  isDark: boolean;
  isReadOnly: boolean;
  badge?: string;
  dense?: boolean;
  required?: boolean;
}) => {
  if (!label) return null;
  return (
    <View style={[styles.labelRow, dense && styles.labelRowDense]}>
      <Text style={[styles.label, dense && styles.labelDense, isDark && styles.labelDark]}>
        {label}
        {required && <Text style={styles.req}> *</Text>}
      </Text>
      {(isReadOnly || badge) && (
        <View style={styles.badgeWrapper}>
          <Text style={styles.badgeText}>{badge ?? "SECURED"}</Text>
        </View>
      )}
    </View>
  );
};

export default function CommonInput({
  label,
  value,
  placeholder,
  error,
  touched = false,
  onChangeText,
  isValid = () => true,
  secureTextEntry = false,
  rightIcon,
  leftIcon,
  disableCopyPaste = false,
  inputProps = {},
  editable = true,
  isDark = false,
  isReadOnly = false,
  badge,
  containerStyle,
  hint,
  multiline = false,
  numberOfLines,
  dense = false,
  required = false,
}: CommonInputProps) {
  const [focused, setFocused] = useState(false);
  const [blurred, setBlurred] = useState(false);
  const { style: customStyle, ...restInputProps } = inputProps;

  const hasValue = value?.trim().length > 0;
  const isInputValid = isValid(value);
  const showError = (!isInputValid || !!error) && (blurred || touched || hasValue);

  let textColor = Colors.ink;
  if (showError) textColor = Colors.danger;

  const getWrapperStyles = () => {
    const activeBorder = isDark ? styles.activeFocusBorderDark : styles.activeFocusBorder;
    const errorBorder = isDark ? styles.errorFocusBorderDark : styles.errorFocusBorder;
    return [
      styles.inputWrapper,
      dense && styles.inputWrapperDense,
      isDark && styles.wrapperDark,
      multiline && styles.wrapperMultiline,
      focused && activeBorder,
      showError && errorBorder,
      (isReadOnly || !editable) && styles.disabledWrapper,
    ];
  };

  return (
    <View style={[styles.container, dense && styles.containerDense, containerStyle]}>
      <InputLabel label={label} isDark={isDark} isReadOnly={isReadOnly} badge={badge} dense={dense} required={required} />

      <View style={[getWrapperStyles(), rightIcon ? styles.wrapperWithRight : undefined]}>
        {leftIcon && <View style={styles.leftIconWrapper}>{leftIcon}</View>}
        <TextInput
          value={value}
          placeholder={placeholder}
          placeholderTextColor={Colors.ink4}
          secureTextEntry={secureTextEntry}
          onChangeText={onChangeText}
          contextMenuHidden={disableCopyPaste}
          editable={editable && !isReadOnly}
          underlineColorAndroid="transparent"
          autoComplete="off"
          autoCorrect={false}
          multiline={multiline}
          numberOfLines={numberOfLines}
          {...(Platform.OS === 'web' ? { autoComplete: 'new-password' } as any : {})}
          style={[
            styles.input,
            dense && styles.inputDense,
            multiline && styles.inputMultiline,
            { color: textColor },
            Platform.OS === 'web' && { outlineStyle: "none", borderWidth: 0 } as any,
            Platform.OS === 'android' && { fontFamily: "", fontWeight: undefined },
            customStyle,
          ]}
          {...restInputProps}
          onFocus={(e) => {
            setFocused(true);
            inputProps.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            setBlurred(true);
            inputProps.onBlur?.(e);
          }}
        />
        {rightIcon && (
          <View style={styles.iconWrapper}>
            {rightIcon}
          </View>
        )}
      </View>
      {showError && !!error ? (
        <Text style={styles.errorText}>{error}</Text>
      ) : (
        !!hint && <Text style={styles.hintText}>{hint}</Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { marginBottom: 18, width: "100%" },
  containerDense: { marginBottom: 0 },
  label: {
    fontWeight: "800",
    color: Colors.ink3,
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 1.5,
  },
  labelDense: { fontSize: 11, letterSpacing: 1 },
  req: { color: Colors.danger, fontWeight: "900", fontSize: 15 },
  labelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
    paddingHorizontal: 4,
  },
  labelRowDense: { marginBottom: 6 },
  badgeWrapper: {
    backgroundColor: Colors.paper2,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: Colors.line,
  },
  badgeText: {
    fontSize: 9,
    fontWeight: "900",
    color: Colors.ink3,
    letterSpacing: 1,
  },
  labelDark: { color: "#9CA3AF" },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    minHeight: 42,
    borderWidth: 1.5,
    borderColor: Colors.line,
    borderRadius: 2,
    backgroundColor: Colors.card,
    paddingHorizontal: 16,
    ...Platform.select({
      ios: { shadowColor: "#000", shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.03, shadowRadius: 4 },
      android: { elevation: 1 },
      web: { transition: "all 0.2s ease-in-out", boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.04)" } as any,
    }),
  },
  inputWrapperDense: {
    minHeight: 34,
    borderWidth: 1.25,
    borderRadius: 10,
    paddingHorizontal: 12,
  },
  wrapperDark: { backgroundColor: "#111827", borderColor: "#1F2937" },
  wrapperMultiline: { minHeight: 96, alignItems: "flex-start", paddingVertical: 12 },
  leftIconWrapper: { justifyContent: "center", alignItems: "center", marginRight: 8 },
  input: {
    flex: 1,
    // Allow the input to shrink below its intrinsic content width so long
    // values stay clipped inside the box instead of overflowing the wrapper
    // (notably in the two-column name row on narrow screens).
    minWidth: 0,
    fontSize: 16,
    fontWeight: "600",
    height: "100%",
    paddingVertical: 0,
    backgroundColor: "transparent",
  },
  inputDense: { fontSize: 13.5, fontWeight: "500" },
  inputMultiline: { height: undefined, paddingVertical: 0, textAlignVertical: "top" },
  activeFocusBorder: { borderColor: Colors.accent, backgroundColor: Colors.card },
  activeFocusBorderDark: { borderColor: Colors.accent },
  errorFocusBorder: { borderColor: Colors.danger, backgroundColor: Colors.dangerSoft },
  errorFocusBorderDark: { borderColor: Colors.danger, backgroundColor: Colors.card },
  disabledWrapper: { backgroundColor: Colors.paper2, borderColor: Colors.line, opacity: 0.7 },
  wrapperWithRight: { paddingRight: 56 },
  iconWrapper: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    width: 52,
  },
  errorText: {
    color: "#EF4444",
    fontSize: 12,
    marginTop: 6,
    fontWeight: "600",
    marginLeft: 4,
  },
  hintText: {
    color: "#94A3B8",
    fontSize: 12,
    marginTop: 6,
    fontWeight: "500",
    marginLeft: 4,
  },
});
